import React, { useState } from "react";
import { Plus, Trash2, Flag, Flame, Star, Minus, CopyPlus, Loader2, Activity, RefreshCw } from "lucide-react";
import { SectionHeading, FieldLabel, StepInstructions } from "@/components/wizard/ui";
import { FieldInfo } from "@/components/wizard/InfoTip";
import { SportIcon } from "@/components/wizard/icons";
import { SPORTS, INTENSITY } from "@/lib/planModel";
import { fmtDuration, fmtDistanceFromMi } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import TpPremiumUpgrade from "@/components/TpPremiumUpgrade";

const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseLocal = (iso) => { const [y, m, dd] = iso.split("-").map(Number); return new Date(y, m - 1, dd); };
const addDaysISO = (iso, n) => { const d = parseLocal(iso); d.setDate(d.getDate() + n); return toISO(d); };
const dayDiff = (a, b) => Math.round((parseLocal(a) - parseLocal(b)) / 86400000);

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const selCls = "h-11 rounded-none bg-[#0A0A0A] border-white/10 text-sm";
const DURATION_CHIPS = [30, 45, 60, 75, 90, 120];
const MAX_SESSIONS = 4;
const WEEK_KEYS = { this: "trainingWeek", next: "nextWeek" };
const WEEK_LABELS = { this: "This week", next: "Next week" };
const emptyWeek = () => ({ days: Array.from({ length: 7 }, () => ({ sessions: [] })) });
const noWheel = (e) => e.currentTarget.blur();

const emptySession = () => ({ sport: "run", duration: 60, intensity: "endurance", timing: "AM", terrain: "flat", heat: false, key: false, mealStyle: "3-meals" });

const SPORT_LABEL = Object.fromEntries(SPORTS.map((s) => [s.value, s.label]));
const INTENSITY_LABEL = Object.fromEntries(INTENSITY.map((i) => [i.value, i.label]));
const INT_COLOR = { recovery: "#A3E635", easy: "#A3E635", endurance: "#22D3EE", steady: "#22D3EE", tempo: "#FBBF24", threshold: "#FB923C", vo2max: "#FB7185", intervals: "#FB7185", long: "#E2E30A", race: "#FB7185", performance: "#FB7185" };
const fmtDur = (min) => { const m = Math.round(min || 0); if (!m) return "—"; const h = Math.floor(m / 60); const mm = m % 60; return h ? `${h}h ${mm}m` : `${mm}m`; };

function DurationField({ di, si, value, onChange }) {
  const set = (v) => onChange(Math.max(0, Math.min(1440, v)));
  return (
    <div>
      <FieldLabel>Duration (mins)</FieldLabel>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`session-duration-minus-${di}-${si}`} onClick={() => set((value || 0) - 15)}
          className="h-11 w-11 shrink-0 border border-white/10 bg-[#0A0A0A] text-white hover:bg-white/5 transition-colors flex items-center justify-center">
          <Minus className="h-4 w-4" />
        </button>
        <Input data-testid={`session-duration-${di}-${si}`} type="number" inputMode="numeric" placeholder="60" onWheel={noWheel}
          className={selCls + " w-20 text-center font-mono font-bold text-base"}
          value={value === 0 ? "" : value}
          onChange={(e) => { const v = e.target.value; onChange(v === "" ? 0 : Math.max(0, Math.min(1440, parseInt(v, 10) || 0))); }} />
        <button type="button" data-testid={`session-duration-plus-${di}-${si}`} onClick={() => set((value || 0) + 15)}
          className="h-11 w-11 shrink-0 border border-white/10 bg-[#0A0A0A] text-white hover:bg-white/5 transition-colors flex items-center justify-center">
          <Plus className="h-4 w-4" />
        </button>
        <div className="flex flex-wrap gap-1.5 ml-1">
          {DURATION_CHIPS.map((m) => (
            <button key={m} type="button" data-testid={`session-duration-chip-${di}-${si}-${m}`} onClick={() => set(m)}
              className={`h-8 px-2.5 text-xs font-semibold border transition-colors ${value === m
                ? "bg-[#E2E30A] text-black border-[#E2E30A]"
                : "bg-transparent text-zinc-200 border-white/15 hover:border-white/40"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Step4Training({ plan, setPlan, patch, onGenerate, regenerating, isLite }) {
  const { raceWeek } = plan;

  const weekDays = (wk) => {
    const w = plan[WEEK_KEYS[wk]];
    return (w && w.days) ? w.days : emptyWeek().days;
  };

  const [importing, setImporting] = useState(false);
  // Drag-and-drop: source session {wk, di, si} while the user is dragging;
  // dropTarget: the day column currently being hovered so we can highlight
  // it. Same-week and cross-week (this ↔ next) moves are supported.
  const [dragFrom, setDragFrom] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const moveSession = (from, toWk, toDi) => {
    if (!from) return;
    if (from.wk === toWk && from.di === toDi) return; // no-op
    setPlan((p) => {
      const next = { ...p };
      const srcKey = WEEK_KEYS[from.wk];
      const dstKey = WEEK_KEYS[toWk];
      const srcDays = (next[srcKey]?.days || []).map((d) => ({ ...d, sessions: [...(d.sessions || [])] }));
      const dstDays = srcKey === dstKey ? srcDays : (next[dstKey]?.days || []).map((d) => ({ ...d, sessions: [...(d.sessions || [])] }));
      const [moved] = srcDays[from.di].sessions.splice(from.si, 1);
      if (!moved) return p;
      // Respect the per-day session cap so athletes don't accidentally
      // stack a 5th session into a day.
      if ((dstDays[toDi].sessions || []).length >= MAX_SESSIONS) {
        toast.warning(`Max ${MAX_SESSIONS} sessions per day — move blocked`);
        return p;
      }
      dstDays[toDi].sessions.push(moved);
      next[srcKey] = { ...(next[srcKey] || {}), days: srcDays };
      if (srcKey !== dstKey) next[dstKey] = { ...(next[dstKey] || {}), days: dstDays };
      return next;
    });
  };
  // Count sessions the TP import couldn't infer a duration for. Surfaced as
  // a red banner + red border so athletes can't accidentally regenerate
  // macros off a zero-minute session.
  const needsInputCount = ["this", "next"].reduce((sum, wk) => {
    const w = plan[WEEK_KEYS[wk]];
    if (!w || !w.days) return sum;
    return sum + w.days.reduce((s, d) => s + (d.sessions || []).filter((x) => x.tpNeedsInput && (!x.duration || x.duration <= 0)).length, 0);
  }, 0);
  const importTP = async () => {
    if (!plan.weekStart) { toast.error("Set your week-start date in Step 1 first"); return; }
    setImporting(true);
    try {
      const start = plan.weekStart;
      // Pull a 2-week window: this week (start) → next week end (start+13),
      // padded by one day either side for DST safety.
      const winStart = addDaysISO(start, -1);
      const winEnd = addDaysISO(start, 14);
      const { data } = await api.get(`/integrations/trainingpeaks/events?start=${winStart}&end=${winEnd}`);
      if (!data.connected) { toast.error("Connect TrainingPeaks in Step 1 first"); return; }
      const allEvs = data.events || [];

      const inRange = (isoStart, isoEnd, evs) => evs.filter((e) => {
        const d = dayDiff(e.date, isoStart);
        return d >= 0 && d <= 6 && e.date <= isoEnd;
      });
      const thisEvs = inRange(start, addDaysISO(start, 6), allEvs);
      const nextEvs = inRange(addDaysISO(start, 7), addDaysISO(start, 13), allEvs);
      const totalFound = thisEvs.length + nextEvs.length;

      if (!totalFound) {
        const dates = allEvs.slice(0, 6).map((e) => e.date).join(", ");
        toast.error(
          allEvs.length
            ? `TP returned ${allEvs.length} session${allEvs.length > 1 ? "s" : ""} (${dates}) — none within This/Next week of ${start}. Check your Week Start in Step 1.`
            : `No TrainingPeaks sessions found in the 2-week window around ${start}. Change your Week Start or add sessions in TP.`
        );
        return;
      }

      const anyExisting = ["trainingWeek", "nextWeek"].some((k) => ((plan[k]?.days) || []).some((d) => (d.sessions || []).length));
      if (anyExisting && !window.confirm("Replace This and Next weeks with your TrainingPeaks sessions? Any manual edits will be overwritten.")) return;

      const paceBySport = data.paceBySport || {};
      const buildDays = (weekStartIso, evs) => {
        const days = Array.from({ length: 7 }, () => ({ sessions: [] }));
        let est = 0;
        let needsInput = 0;
        evs.forEach((e) => {
          const di = dayDiff(e.date, weekStartIso);
          if (di < 0 || di > 6 || days[di].sessions.length >= MAX_SESSIONS) return;
          let duration = e.durationMin || 0;
          let estimated = false;
          if (!duration && e.distanceMi && paceBySport[e.sport]) {
            duration = Math.round(e.distanceMi * paceBySport[e.sport]);
            estimated = true;
          } else if (!duration) {
            estimated = true;
          }
          // A "needs-input" session is anything TP gave us with no duration
          // AND no distance we could pace-estimate from. We flag these so
          // Step 4 can highlight them and refuse to build macros off zeros.
          const tpNeedsInput = !duration && !e.distanceMi;
          if (estimated) est += 1;
          if (tpNeedsInput) needsInput += 1;
          days[di].sessions.push({
            sport: e.sport || "other",
            duration: Math.max(0, Math.min(1440, duration)),
            intensity: e.intensity || "endurance",
            timing: "AM", terrain: "flat", heat: false, key: false, mealStyle: "3-meals",
            tpEstimated: estimated,
            tpNeedsInput,
            tpDistanceMi: e.distanceMi || null,
            tpTitle: e.title || "",
          });
        });
        return { days, est, needsInput };
      };

      const prevBuild = { days: [], est: 0 }; void prevBuild;
      const thisBuild = buildDays(start, thisEvs);
      const nextBuild = buildDays(addDaysISO(start, 7), nextEvs);
      const estCount = thisBuild.est + nextBuild.est;
      const needsInputCount = thisBuild.needsInput + nextBuild.needsInput;

      setPlan((p) => ({
        ...p,
        trainingWeek: { ...(p.trainingWeek || {}), days: thisBuild.days },
        nextWeek: { ...(p.nextWeek || {}), days: nextBuild.days },
      }));

      const parts = [
        `This ${thisEvs.length}`,
        `Next ${nextEvs.length}`,
      ].join(" · ");
      toast.success(`Imported ${totalFound} session${totalFound > 1 ? "s" : ""} — ${parts}${estCount ? ` · ${estCount} time-estimate${estCount > 1 ? "s" : ""} to check` : ""}`);
      if (needsInputCount) {
        toast.warning(`${needsInputCount} session${needsInputCount > 1 ? "s" : ""} came in with no time or distance — fill them in below before regenerating macros.`, { duration: 12000 });
      }
      toast.message("Generate a fresh plan to update macros, meals and pathways for the new training.", { duration: 8000 });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not import from TrainingPeaks"); }
    finally { setImporting(false); }
  };

  // Handlers now take the week key up front so a click on any of the 3 blocks
  // writes to the correct plan slice regardless of which block was expanded.
  const updateDays = (wk, fn) => setPlan((p) => {
    const key = WEEK_KEYS[wk];
    const currentDays = (p[key] && p[key].days) ? p[key].days : emptyWeek().days;
    return { ...p, [key]: { ...(p[key] || {}), days: fn(currentDays) } };
  });

  const addSession = (wk, di) => updateDays(wk, (ds) => ds.map((d, i) =>
    i === di && d.sessions.length < MAX_SESSIONS ? { ...d, sessions: [...d.sessions, emptySession()] } : d));
  const removeSession = (wk, di, si) => updateDays(wk, (ds) => ds.map((d, i) =>
    i === di ? { ...d, sessions: d.sessions.filter((_, j) => j !== si) } : d));
  const updateSession = (wk, di, si, key, val) => updateDays(wk, (ds) => ds.map((d, i) =>
    i === di ? { ...d, sessions: d.sessions.map((s, j) => j === si ? { ...s, [key]: val } : s) } : d));
  const copyPrevDay = (wk, di) => updateDays(wk, (ds) => ds.map((d, i) =>
    i === di ? { ...d, sessions: ds[di - 1].sessions.map((s) => ({ ...s })) } : d));

  const copyFromThis = (wk) => {
    const source = weekDays("this");
    if (!window.confirm(`Overwrite ${WEEK_LABELS[wk]} with a copy of this week's sessions?`)) return;
    setPlan((p) => ({ ...p, [WEEK_KEYS[wk]]: { ...(p[WEEK_KEYS[wk]] || {}), days: source.map((d) => ({ ...d, sessions: (d.sessions || []).map((s) => ({ ...s })) })) } }));
    toast.success(`${WEEK_LABELS[wk]} filled from this week`);
  };

  const promoteNextWeek = () => {
    if (!window.confirm("Move next week's sessions to become this week? This week's current sessions will be cleared.")) return;
    setPlan((p) => ({
      ...p,
      trainingWeek: p.nextWeek?.days ? { ...p.nextWeek } : emptyWeek(),
      nextWeek: emptyWeek(),
    }));
    toast.success("Rolled the weeks forward — macros will refresh");
  };

  const [editing, setEditing] = useState(null); // { wk, di, si }
  const addAndEdit = (wk, di) => {
    const wkDays = weekDays(wk);
    const si = wkDays[di].sessions.length;
    if (si >= MAX_SESSIONS) return;
    addSession(wk, di);
    setEditing({ wk, di, si });
  };

  return (
    <div>
      <SectionHeading eyebrow="Step 05" title="Training Weeks"
        desc="Last week, this week and next week — this week (highlighted) drives your macros; the others let you look back and plan ahead." />

      <StepInstructions>
        This week is always in the middle, highlighted in yellow. Look back at last week or sketch next week — copy from this week with the small button on each block. Racing? Set that session's intensity to <span className="text-[#E2E30A]">Race</span> and the plan will auto-detect race day and carb-load the 3 days before.
      </StepInstructions>

      {needsInputCount > 0 && (
        <div data-testid="tp-needs-input-banner"
          className="mb-4 border border-[#FF3B30]/60 bg-[#FF3B30]/[0.08] px-3 sm:px-4 py-3 flex items-start gap-2.5">
          <Flame className="h-4 w-4 text-[#FF3B30] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-[#FF3B30]">
              {needsInputCount} TrainingPeaks session{needsInputCount > 1 ? "s need" : " needs"} time or distance
            </p>
            <p className="text-[11px] text-zinc-100 mt-1 leading-relaxed">
              Those sessions came in with no duration and no distance — tap the red cards below and add a duration so macros can be built correctly.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {plan.profile?.useTP !== false && (
          <>
            <Button data-testid="tp-import-btn" onClick={importTP} disabled={importing}
              className="rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black text-xs font-semibold uppercase tracking-wider">
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Activity className="h-4 w-4 mr-1.5" />} Import TrainingPeaks
            </Button>
          </>
        )}
        {onGenerate && (
          <Button data-testid="step4-regenerate-btn" onClick={async () => {
            try {
              await onGenerate();
              toast.success("Plan regenerated — macros, meals & pathways updated");
            } catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate"); }
          }} disabled={regenerating || importing}
            className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs font-semibold uppercase tracking-wider">
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Regenerate plan
          </Button>
        )}
        {plan.profile?.useTP !== false ? (
          <>
            <span className="text-[11px] text-zinc-400">Pulls your connected TrainingPeaks sessions for the 3-week window straight into the blocks below. <b className="text-[#E2E30A]">Then tap Regenerate</b> to rebuild macros, meals and pathways from the new training. <span className="text-[#FFB454]">Calendar Sync requires a TrainingPeaks Premium account.</span></span>
            <TpPremiumUpgrade className="ml-auto" />
          </>
        ) : (
          <span className="text-[11px] text-zinc-400 font-mono">
            TrainingPeaks skipped in your profile. <a href="#" onClick={(e) => { e.preventDefault(); toast("Head to Step 1 → Do you use TrainingPeaks? — say Yes."); }} className="text-[#FFB454] underline decoration-dotted">Enable it in Step 1</a> if you change your mind.
          </span>
        )}
      </div>

      {plan.profile?.useTP !== false && (
        <div data-testid="tp-sync-notice" className="mb-4 border border-[#FF9500]/45 bg-[#FF9500]/[0.08] px-3 sm:px-4 py-3 flex items-start gap-2.5">
          <Flame className="h-4 w-4 text-[#FF9500] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-[#FF9500]">TrainingPeaks Sync</p>
            <p className="text-[11px] text-zinc-100 mt-1 leading-relaxed">
              Changes made to sessions in TrainingPeaks may take up to <b className="text-white">24 hours</b> to appear here. This includes sessions that are moved, edited or deleted.
              {" "}<b className="text-[#E2E30A]">Tip:</b> you can drag any session card between days (or to Next Week) to reshuffle without waiting for TP.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {["this", "next"].map((wk) => {
          const isThis = wk === "this";
          const wkDays = weekDays(wk);
          const total = wkDays.reduce((m, d) => m + (d.sessions || []).reduce((s, x) => s + (parseInt(x.duration, 10) || 0), 0), 0);
          return (
            <div key={wk} data-testid={`week-block-${wk}`}
              className={`border bg-[#141414] ${isThis
                ? "border-[#E2E30A] shadow-[0_0_0_1px_rgba(226,227,10,0.4)]"
                : "border-white/10 opacity-95"}`}>
              <div className={`flex items-center flex-wrap gap-2 px-3 sm:px-4 py-3 ${isThis ? "bg-[#E2E30A]/[0.06]" : ""}`}>
                <span className={`text-[10px] uppercase tracking-[0.3em] font-bold ${isThis ? "text-[#E2E30A]" : "text-zinc-400"}`}>
                  {WEEK_LABELS[wk]}
                </span>
                {total > 0 && (
                  <span className="text-[10px] font-mono text-zinc-300 ml-auto">
                    Total <span className={isThis ? "text-white" : "text-zinc-100"}>{fmtDur(total)}</span>
                  </span>
                )}
                <div className={total > 0 ? "" : "ml-auto"} />
                {!isThis && (
                  <button type="button" data-testid={`copy-this-to-${wk}`} onClick={() => copyFromThis(wk)}
                    className="text-[10px] uppercase tracking-wider text-zinc-100 border border-white/20 hover:border-white/40 px-2.5 py-1">
                    Copy from this week
                  </button>
                )}
                {wk === "next" && (
                  <button type="button" data-testid="promote-next-week" onClick={promoteNextWeek}
                    className="text-[10px] uppercase tracking-wider text-black bg-[#4C9AFF] hover:bg-[#3a86e6] px-2.5 py-1 font-bold">
                    Use as this week
                  </button>
                )}
              </div>
              <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2 p-2 sm:p-3 ${isThis ? "" : "opacity-90"}`}>
                {wkDays.map((day, di) => {
                  const isRace = isThis && raceWeek.enabled && raceWeek.dayIndex === di;
                  const prevHasSessions = di > 0 && wkDays[di - 1].sessions.length > 0;
                  const dayMin = day.sessions.reduce((s, x) => s + (x.duration || 0), 0);
                  // Compute the calendar date for this cell so athletes can
                  // eyeball "Sat 19 Sep" instead of just "SAT" — beta
                  // feedback Feb 27 2026 "be good to add the dates to the
                  // calendar". Uses plan.weekStart as day 0; Next Week is
                  // weekStart + 7. Falls back gracefully if weekStart is
                  // missing so nothing renders as "undefined".
                  const weekBaseIso = wk === "next" && plan.weekStart ? addDaysISO(plan.weekStart, 7) : plan.weekStart;
                  const cellIso = weekBaseIso ? addDaysISO(weekBaseIso, di) : null;
                  const cellDate = cellIso ? parseLocal(cellIso) : null;
                  const cellLabel = cellDate ? cellDate.toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;
                  return (
                    <div key={DAYS[di] || di} data-testid={`training-day-${wk}-${di}`}
                      onDragOver={(e) => { if (dragFrom) { e.preventDefault(); setDropTarget({ wk, di }); } }}
                      onDragLeave={() => setDropTarget((t) => (t && t.wk === wk && t.di === di ? null : t))}
                      onDrop={(e) => { e.preventDefault(); moveSession(dragFrom, wk, di); setDragFrom(null); setDropTarget(null); }}
                      className={`border bg-[#0A0A0A] p-2 flex flex-col min-h-[150px] transition-colors ${
                        dropTarget && dropTarget.wk === wk && dropTarget.di === di
                          ? "border-[#E2E30A] bg-[#E2E30A]/[0.08]"
                          : (isRace ? "border-[#FF3B30]/50" : "border-white/10")}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-display font-bold text-sm uppercase tracking-wide leading-none ${isThis ? "text-white" : "text-zinc-300"}`}>
                          {DAYS[di].slice(0, 3)}
                          {cellLabel && <span className="ml-1 text-[10px] font-mono text-zinc-500 uppercase tracking-wider">{cellLabel}</span>}
                        </span>
                        {isRace ? <Flag className="h-3 w-3 text-[#FF3B30]" /> : (dayMin > 0 && <span className="text-[9px] font-mono text-zinc-400" data-testid={`training-day-total-${wk}-${di}`}>{fmtDur(dayMin)}</span>)}
                      </div>

                      <div className="space-y-1.5 flex-1">
                        {day.sessions.map((s, si) => {
                          const color = INT_COLOR[s.intensity] || "#4C9AFF";
                          const needsInput = s.tpNeedsInput && (!s.duration || s.duration <= 0);
                          const isDragged = dragFrom && dragFrom.wk === wk && dragFrom.di === di && dragFrom.si === si;
                          return (
                            <button key={si} type="button" data-testid={`session-card-${wk}-${di}-${si}`}
                              draggable
                              onDragStart={(e) => { setDragFrom({ wk, di, si }); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `${wk}:${di}:${si}`); } catch { /* older browsers */ } }}
                              onDragEnd={() => { setDragFrom(null); setDropTarget(null); }}
                              onClick={() => setEditing({ wk, di, si })}
                              title="Click to edit · Drag to move"
                              className={`w-full text-left border p-2 transition-colors cursor-grab active:cursor-grabbing ${isDragged ? "opacity-40" : ""} ${needsInput
                                ? "border-[#FF3B30] bg-[#FF3B30]/[0.08] hover:border-[#FF3B30]"
                                : "border-white/10 bg-[#141414] hover:border-white/30"}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <SportIcon sport={s.sport} className={`h-3.5 w-3.5 shrink-0 ${needsInput ? "text-[#FF3B30]" : "text-[#E2E30A]"}`} />
                                <span className="text-[11px] text-white truncate">{SPORT_LABEL[s.sport] || s.sport}</span>
                              </div>
                              {s.tpTitle && s.tpTitle.toLowerCase() !== (SPORT_LABEL[s.sport] || s.sport).toLowerCase() && (
                                <div data-testid={`session-tp-title-${wk}-${di}-${si}`}
                                  className="text-[10px] text-zinc-300 truncate mb-1 leading-tight" title={s.tpTitle}>
                                  {s.tpTitle}
                                </div>
                              )}
                              <div className={`text-[10px] font-mono ${needsInput ? "text-[#FF3B30] font-bold" : "text-zinc-300"}`} data-testid={`session-time-${wk}-${di}-${si}`}>
                                {needsInput ? "Add time" : `${fmtDur(s.duration)}${s.tpDistanceMi ? ` · ${fmtDistanceFromMi(s.tpDistanceMi, plan.profile?.unitSystem)}` : ""}`}
                              </div>
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                <span className="text-[8px] uppercase tracking-wide px-1.5 py-0.5 font-semibold" style={{ color: "#F5F1E8", border: `1px solid ${color}` }}>{INTENSITY_LABEL[s.intensity] || s.intensity}</span>
                                {s.heat && <Flame className="h-2.5 w-2.5 text-[#FF9500]" />}
                                {s.key && <Star className="h-2.5 w-2.5 text-[#E2E30A]" />}
                                {needsInput ? (
                                  <span data-testid={`session-needs-input-${wk}-${di}-${si}`} className="text-[8px] uppercase tracking-wide text-[#FF3B30] font-bold">Needs time</span>
                                ) : (s.tpEstimated && <span data-testid={`session-est-${wk}-${di}-${si}`} className="text-[8px] text-[#FF9500]">est</span>)}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <div className="mt-2 flex flex-col gap-1">
                        {day.sessions.length === 0 && prevHasSessions && (
                          <Button data-testid={`training-copy-prev-${wk}-${di}`} variant="ghost" size="sm" onClick={() => copyPrevDay(wk, di)}
                            className="rounded-none text-zinc-300 hover:bg-white/5 uppercase text-[10px] tracking-wider h-7 px-1">
                            <CopyPlus className="h-3 w-3 mr-1" /> Copy {DAYS[di - 1].slice(0, 3)}
                          </Button>
                        )}
                        {day.sessions.length < MAX_SESSIONS && (
                          <Button data-testid={`training-add-session-${wk}-${di}`} size="sm" onClick={() => addAndEdit(wk, di)}
                            className="rounded-none bg-transparent border border-[#4C9AFF]/40 text-[#4C9AFF] hover:bg-[#4C9AFF]/10 uppercase text-[10px] tracking-wider h-7 px-1">
                            <Plus className="h-3 w-3 mr-1" /> Add
                          </Button>
                        )}
                        {/* Quick-flag icons — tap directly under Add to
                            flag the day's first session as Race / Heat /
                            Key without opening the editor. Bright when
                            active, dim when off. If the day has no
                            session, tapping any icon spawns one with
                            that flag pre-set. */}
                        {(() => {
                          const first = day.sessions[0];
                          const isRace = first?.intensity === "race";
                          const isHeat = !!first?.heat;
                          const isKey = !!first?.key;
                          const toggleFlag = (kind) => {
                            if (!first) {
                              // No session yet — create one and apply the flag.
                              addSession(wk, di);
                              setTimeout(() => {
                                if (kind === "race") updateSession(wk, di, 0, "intensity", "race");
                                else updateSession(wk, di, 0, kind, true);
                              }, 0);
                              return;
                            }
                            if (kind === "race") updateSession(wk, di, 0, "intensity", isRace ? "endurance" : "race");
                            else updateSession(wk, di, 0, kind, kind === "heat" ? !isHeat : !isKey);
                          };
                          return (
                            <div className="flex items-center justify-around gap-1 mt-0.5" data-testid={`day-flags-${wk}-${di}`}>
                              <button type="button" title={isRace ? "Race · tap to unset" : "Mark as Race day"}
                                data-testid={`day-flag-race-${wk}-${di}`}
                                onClick={(e) => { e.stopPropagation(); toggleFlag("race"); }}
                                className={`p-1 border transition-colors ${isRace ? "border-[#FF3B30] bg-[#FF3B30]/15" : "border-white/10 hover:border-[#FF3B30]/50"}`}>
                                <Flag className={`h-3 w-3 ${isRace ? "text-[#FF3B30]" : "text-zinc-500"}`} />
                              </button>
                              <button type="button" title={isHeat ? "Heat · tap to unset" : "Mark as Heat session"}
                                data-testid={`day-flag-heat-${wk}-${di}`}
                                onClick={(e) => { e.stopPropagation(); toggleFlag("heat"); }}
                                className={`p-1 border transition-colors ${isHeat ? "border-[#FF9500] bg-[#FF9500]/15" : "border-white/10 hover:border-[#FF9500]/50"}`}>
                                <Flame className={`h-3 w-3 ${isHeat ? "text-[#FF9500]" : "text-zinc-500"}`} />
                              </button>
                              <button type="button" title={isKey ? "Key session · tap to unset" : "Mark as Key session"}
                                data-testid={`day-flag-key-${wk}-${di}`}
                                onClick={(e) => { e.stopPropagation(); toggleFlag("key"); }}
                                className={`p-1 border transition-colors ${isKey ? "border-[#E2E30A] bg-[#E2E30A]/15" : "border-white/10 hover:border-[#E2E30A]/50"}`}>
                                <Star className={`h-3 w-3 ${isKey ? "text-[#E2E30A]" : "text-zinc-500"}`} />
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Session editor */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-[#141414] border-white/10 rounded-none text-white max-w-lg">
          {editing && weekDays(editing.wk)[editing.di]?.sessions[editing.si] && (() => {
            const { wk, di, si } = editing;
            const s = weekDays(wk)[di].sessions[si];
            return (
              <div data-testid="session-editor">
                <DialogHeader>
                  <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
                    <SportIcon sport={s.sport} className="h-5 w-5 text-[#E2E30A]" /> {WEEK_LABELS[wk]} · {DAYS[di]} · Session {si + 1}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <div>
                    <FieldLabel>Sport</FieldLabel>
                    <Select value={s.sport} onValueChange={(v) => updateSession(wk, di, si, "sport", v)}>
                      <SelectTrigger data-testid={`session-sport-${di}-${si}`} className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10 rounded-none">
                        {SPORTS.map((sp) => <SelectItem key={sp.value} value={sp.value}>{sp.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel info={
                      <FieldInfo testId={`info-session-type-${di}-${si}`} title="Session type / intensity">
                        Sets how hard the session is, which drives its energy cost and carbohydrate demand — from Recovery (easiest) up to Race pace.
                      </FieldInfo>
                    }>Session Type</FieldLabel>
                    <Select value={s.intensity} onValueChange={(v) => updateSession(wk, di, si, "intensity", v)}>
                      <SelectTrigger data-testid={`session-intensity-${di}-${si}`} className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10 rounded-none">
                        {INTENSITY.map((it) => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-3">
                  <DurationField di={di} si={si} value={s.duration} onChange={(v) => {
                    updateSession(wk, di, si, "duration", v);
                    // Athlete filled in a real duration — clear the "needs input" flag
                    // so the red highlight and top banner disappear automatically.
                    if (v > 0 && s.tpNeedsInput) updateSession(wk, di, si, "tpNeedsInput", false);
                  }} />
                  {s.tpNeedsInput && (!s.duration || s.duration <= 0) && (
                    <div data-testid={`session-editor-needs-input-${di}-${si}`}
                      className="mt-2 border border-[#FF3B30]/60 bg-[#FF3B30]/[0.08] px-3 py-2">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#FF3B30]">Time / distance needed</p>
                      <p className="text-[11px] text-zinc-100 mt-0.5 leading-relaxed">TrainingPeaks didn't include a duration for this session. Add it above so we can build macros correctly.</p>
                    </div>
                  )}
                  {!s.tpNeedsInput && s.tpEstimated && <p className="text-[11px] text-[#FF9500] mt-1">Estimated from your recent pace — adjust if you know the real time.</p>}
                  {s.tpDistanceMi ? <p className="text-[11px] text-zinc-400 mt-1">TrainingPeaks distance: {fmtDistanceFromMi(s.tpDistanceMi, plan.profile?.unitSystem)}</p> : null}
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-3 items-end">
                  <div>
                    <FieldLabel>Timing</FieldLabel>
                    <Select value={s.timing} onValueChange={(v) => updateSession(wk, di, si, "timing", v)}>
                      <SelectTrigger data-testid={`session-timing-${di}-${si}`} className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10 rounded-none">
                        {["AM", "Midday", "PM"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel info={
                      <FieldInfo testId={`info-flags-${di}-${si}`} title="Race / Heat / Key">
                        <div><b className="text-white">Race</b> — this session is a race. Auto-triggers carb-loading over the 3 days before.</div>
                        <div><b className="text-white">Heat</b> — hot conditions; adds hydration & electrolytes.</div>
                        <div><b className="text-white">Key</b> — priority workout; raises demand and protects fuelling.</div>
                      </FieldInfo>
                    }>Flags</FieldLabel>
                    <div className="flex items-center gap-4 h-11 flex-wrap">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch data-testid={`session-race-${di}-${si}`} checked={s.intensity === "race"}
                          onCheckedChange={(v) => updateSession(wk, di, si, "intensity", v ? "race" : "endurance")} />
                        <span className="text-sm text-zinc-200 flex items-center gap-1"><Flag className="h-3.5 w-3.5 text-[#FF3B30]" /> Race</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch data-testid={`session-heat-${di}-${si}`} checked={s.heat} onCheckedChange={(v) => updateSession(wk, di, si, "heat", v)} />
                        <span className="text-sm text-zinc-200 flex items-center gap-1"><Flame className="h-3.5 w-3.5 text-[#FF9500]" /> Heat</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch data-testid={`session-key-${di}-${si}`} checked={!!s.key} onCheckedChange={(v) => updateSession(wk, di, si, "key", v)} />
                        <span className="text-sm text-zinc-200 flex items-center gap-1"><Star className="h-3.5 w-3.5 text-[#E2E30A]" /> Key</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-5">
                  <Button data-testid={`session-remove-${di}-${si}`} variant="ghost" onClick={() => { removeSession(wk, di, si); setEditing(null); }}
                    className="rounded-none text-[#FF3B30] hover:bg-[#FF3B30]/10 uppercase text-xs tracking-wider">
                    <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                  </Button>
                  <Button data-testid="session-editor-done" onClick={() => setEditing(null)}
                    className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black uppercase text-xs tracking-wider font-semibold">Done</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>



      {/* Race Week Panel removed Feb 2026 — race day is now auto-detected
          from any session with intensity="race" (see engine.compute_targets).
          Removes the double-entry annoyance of ticking a race here AND
          entering a race session in the calendar above. */}
    </div>
  );
}
